<script setup lang="ts">
import { Head, Link, router, useForm } from "@inertiajs/vue3"
import { Building2, Pencil, Plus, Search, SlidersHorizontal, Trash2, X } from "lucide-vue-next"
import { computed, onUnmounted, ref, watch } from "vue"


import AddressForm, { type AddressFields } from "@/components/AddressForm.vue"
import AppDataTable from "@/components/AppDataTable.vue"
import type { TableColumn } from "@/components/AppDataTable.vue"
import AppDialog from "@/components/AppDialog.vue"
import AppPagination from "@/components/AppPagination.vue"
import FormSection from "@/components/FormSection.vue"
import InputError from "@/components/InputError.vue"
import RelativeTime from "@/components/RelativeTime.vue"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAuth } from "@/composables/useAuth"
import { useDialog } from "@/composables/useDialog"
import AppLayout from "@/layouts/AppLayout.vue"
import {
  editOrganizationPath,
  organizationPath,
  organizationsPath,
} from "@/routes"
import { type BreadcrumbItem } from "@/types"

interface Address {
  id?: number
  unit_no: string | null
  street_no: string
  street_name: string
  street_type: string
  city: string
  state: string
  postcode: string
}

interface Organization {
  id: number
  name: string
  contact: string
  email: string
  phone: string
  active: boolean
  created_at: string
  address: Address | null
}

interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Filters {
  q?: string | null
  active?: string | null
  sort?: string | null
  direction?: string | null
}

const props = defineProps<{ organizations: Organization[]; pagination: Pagination; filters: Filters }>()

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Organizations", href: organizationsPath() },
]

// ── Search & filter state ─────────────────────────────────────────────────────
const ALL = "__all__"

const search = ref(props.filters.q ?? "")
const activeFilter = ref(props.filters.active ?? ALL)
const sortCol = ref(props.filters.sort ?? "")
const sortDir = ref<"asc" | "desc">((props.filters.direction as "asc" | "desc") ?? "desc")

const hasActiveFilters = computed(
  () => search.value !== "" || activeFilter.value !== ALL,
)

const filterParams = computed(() => ({
  q: search.value || undefined,
  active: activeFilter.value !== ALL ? activeFilter.value : undefined,
  sort: sortCol.value || undefined,
  direction: sortCol.value ? sortDir.value : undefined,
}))

const tableColumns: TableColumn[] = [
  { key: "name", label: "Name", sortable: true },
  { key: "contact", label: "Contact" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "status", label: "Status" },
  { key: "created_at", label: "Created", sortable: true },
  { key: "actions", label: "Actions", align: "right" },
]

let searchTimer: ReturnType<typeof setTimeout>

function applyFilters() {
  const params = Object.fromEntries(
    Object.entries(filterParams.value).filter(([, v]) => v != null),
  )
  router.get(organizationsPath(), params, { preserveState: true, replace: true })
}

onUnmounted(() => clearTimeout(searchTimer))

watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(applyFilters, 350)
})

watch(activeFilter, applyFilters)

function clearFilters() {
  search.value = ""
  activeFilter.value = ALL
  sortCol.value = ""
  sortDir.value = "desc"
}

function handleSort(key: string, dir: "asc" | "desc") {
  sortCol.value = key
  sortDir.value = dir
  applyFilters()
}

// ── Create dialog state ───────────────────────────────────────────────────────
const { isAdmin } = useAuth()
const { confirm } = useDialog()

const showCreateDialog = ref(false)

// ── Create form ───────────────────────────────────────────────────────────────
const form = useForm({
  name: "",
  contact: "",
  email: "",
  phone: "",
  address_attributes: {
    id: undefined as number | undefined,
    unit_no: "",
    street_no: "",
    street_name: "",
    street_type: "",
    city: "",
    state: "",
    postcode: "",
  },
})

function openCreate() {
  form.reset()
  showCreateDialog.value = true
}

function submitCreate() {
  form.post(organizationsPath(), {
    preserveScroll: true,
    onSuccess: () => {
      showCreateDialog.value = false
    },
  })
}

// Bridge form.address_attributes ↔ AddressForm component
const createAddressFields = computed<AddressFields>({
  get: () => ({
    unit_no: form.address_attributes.unit_no,
    street_no: form.address_attributes.street_no,
    street_name: form.address_attributes.street_name,
    street_type: form.address_attributes.street_type,
    city: form.address_attributes.city,
    state: form.address_attributes.state,
    postcode: form.address_attributes.postcode,
  }),
  set: (val) => Object.assign(form.address_attributes, val),
})

// ── Delete ────────────────────────────────────────────────────────────────────
async function confirmDelete(org: Organization) {
  const ok = await confirm({
    title: 'Delete organization?',
    description: `Are you sure you want to delete ${org.name}? This action cannot be undone.`,
    confirmLabel: 'Delete',
  })
  if (!ok) return
  router.delete(organizationPath(org.id), { preserveScroll: true })
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function err(field: string): string[] | undefined {
  const msg = (form.errors as Record<string, string | undefined>)[field]
  return msg ? [msg] : undefined
}
</script>

<template>
  <Head title="Organizations" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="flex h-full flex-1 flex-col gap-4 overflow-x-auto p-4">
      <!-- Page header -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Building2 class="text-muted-foreground size-6" />
          <div>
            <h1 class="text-foreground text-xl font-semibold">Organizations</h1>
            <p class="text-muted-foreground text-sm">
              Manage your partner organizations
            </p>
          </div>
        </div>
        <Button @click="openCreate">
          <Plus class="size-4" />
          New Organization
        </Button>
      </div>

      <!-- Table card -->
      <Card class="border-sidebar-border/70 dark:border-sidebar-border">
        <CardHeader class="pb-3">
          <div class="flex items-center justify-between">
            <div>
              <CardTitle class="text-base">All Organizations</CardTitle>
              <CardDescription>
                {{ pagination.total_count }} organization{{
                  pagination.total_count !== 1 ? "s" : ""
                }}{{ hasActiveFilters ? " found" : " total" }}
              </CardDescription>
            </div>
            <SlidersHorizontal class="text-muted-foreground size-4" />
          </div>
        </CardHeader>

        <!-- Search & filters -->
        <div class="border-border border-b px-4 pb-4">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div class="relative flex-1">
              <Search class="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                v-model="search"
                placeholder="Search by name, contact or email…"
                class="pl-9"
              />
            </div>

            <Select v-model="activeFilter">
              <SelectTrigger class="w-full sm:w-36">
                <SelectValue placeholder="Any status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem :value="ALL">Any status</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>

            <Button
              v-if="hasActiveFilters"
              variant="ghost"
              size="sm"
              class="text-muted-foreground hover:text-foreground shrink-0"
              @click="clearFilters"
            >
              <X class="size-4" />
              Clear
            </Button>
          </div>
        </div>

        <CardContent class="p-0">
          <!-- Empty state -->
          <div
            v-if="organizations.length === 0"
            class="flex flex-col items-center justify-center gap-3 py-16 text-center"
          >
            <Building2 class="text-muted-foreground/40 size-12" />
            <p class="text-muted-foreground text-sm">
              {{ hasActiveFilters ? "No organizations match your filters" : "No organizations yet" }}
            </p>
            <Button v-if="hasActiveFilters" variant="outline" size="sm" @click="clearFilters">
              <X class="size-4" />
              Clear filters
            </Button>
            <Button v-else variant="outline" size="sm" @click="openCreate">
              <Plus class="size-4" />
              Add your first organization
            </Button>
          </div>

          <!-- Table -->
          <AppDataTable
            v-else
            :columns="tableColumns"
            :sort="sortCol || undefined"
            :direction="sortDir"
            @sort="handleSort"
          >
            <tr
              v-for="org in organizations"
              :key="org.id"
              class="border-border hover:bg-muted/50 border-b transition-colors last:border-0"
            >
              <td class="px-6 py-4 font-medium">
                <Link
                  :href="editOrganizationPath(org.id)"
                  class="text-foreground hover:text-primary hover:underline"
                >{{ org.name }}</Link>
              </td>
              <td class="text-muted-foreground px-6 py-4">{{ org.contact }}</td>
              <td class="text-muted-foreground px-6 py-4">{{ org.email }}</td>
              <td class="text-muted-foreground px-6 py-4">{{ org.phone }}</td>
              <td class="px-6 py-4">
                <Badge :variant="org.active ? 'default' : 'secondary'">
                  {{ org.active ? "Active" : "Inactive" }}
                </Badge>
              </td>
              <td class="text-muted-foreground px-6 py-4">
                <RelativeTime :date="org.created_at" />
              </td>
              <td class="px-6 py-4">
                <div class="flex items-center justify-end gap-2">
                  <Button variant="ghost" size="icon" class="size-8" as-child>
                    <Link :href="editOrganizationPath(org.id)">
                      <Pencil class="size-4" />
                      <span class="sr-only">Edit</span>
                    </Link>
                  </Button>
                  <Button
                    v-if="isAdmin"
                    variant="ghost"
                    size="icon"
                    class="text-destructive hover:text-destructive size-8"
                    @click="confirmDelete(org)"
                  >
                    <Trash2 class="size-4" />
                    <span class="sr-only">Delete</span>
                  </Button>
                </div>
              </td>
            </tr>
          </AppDataTable>
          <!-- Pagination -->
          <AppPagination
            v-if="pagination.total_pages > 1"
            :current-page="pagination.current_page"
            :total-pages="pagination.total_pages"
            :total-count="pagination.total_count"
            :per-page="pagination.per_page"
            :base-url="organizationsPath()"
            :extra-params="filterParams"
          />
        </CardContent>
      </Card>
    </div>

    <!-- ── Create Dialog ─────────────────────────────────────────────────── -->
    <AppDialog
      v-model:open="showCreateDialog"
      title="New Organization"
      description="Fill in the details to create a new organization."
      scrollable
      class="sm:max-w-lg"
    >
      <div class="space-y-4">
        <FormSection title="Contact Information">
          <div class="grid gap-2">
            <Label for="c-name">Name</Label>
            <Input id="c-name" v-model="form.name" placeholder="Acme Corp" />
            <InputError :messages="err('name')" />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div class="grid gap-2">
              <Label for="c-contact">Contact Person</Label>
              <Input id="c-contact" v-model="form.contact" placeholder="John Smith" />
              <InputError :messages="err('contact')" />
            </div>
            <div class="grid gap-2">
              <Label for="c-phone">Phone</Label>
              <Input id="c-phone" v-model="form.phone" type="tel" placeholder="+61 400 000 000" />
              <InputError :messages="err('phone')" />
            </div>
          </div>

          <div class="grid gap-2">
            <Label for="c-email">Email</Label>
            <Input id="c-email" v-model="form.email" type="email" placeholder="contact@acme.com" />
            <InputError :messages="err('email')" />
          </div>
        </FormSection>

        <FormSection title="Address" :optional="true">
          <AddressForm v-model="createAddressFields" :errors="form.errors" error-prefix="address." />
        </FormSection>
      </div>
      <template #footer>
        <Button variant="outline" @click="showCreateDialog = false">Cancel</Button>
        <Button :disabled="form.processing" @click="submitCreate">Create Organization</Button>
      </template>
    </AppDialog>

  </AppLayout>
</template>
